// ============================================================================
// CASE GROUP CANDIDATE PICKER — add eligible cases as members (Phase A)
// ============================================================================
// Per-group-type filter is applied server-side (e.g. AVAILABLE_FOR_AUCTION for
// auction groups). UI just hands the user a checkbox list of candidates.

import React, { useCallback, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import notificationService from 'services/notificationService';
import { caseGroupService } from 'api/services/caseGroupService';
import { useCaseGroupCandidates } from 'hooks/api/useCaseGroups';
import type { CaseGroupCandidate } from 'api/types/caseGroup.types';

export interface CaseGroupCandidatePickerModalProps {
  isOpen: boolean;
  groupId: string;
  onAdded: () => void;
  onClose: () => void;
}

const fmtAmount = (n?: number | null) =>
  n == null ? '—' : `₹${Number(n).toLocaleString('en-IN')}`;

const candidateLabel = (c: CaseGroupCandidate) => {
  if (c.vehicleRegistrationNo) {
    const mm = [c.vehicleMake, c.vehicleModel].filter(Boolean).join(' ');
    return mm ? `${c.vehicleRegistrationNo} · ${mm}` : c.vehicleRegistrationNo;
  }
  return c.caseCode;
};

const CaseGroupCandidatePickerModal: React.FC<CaseGroupCandidatePickerModalProps> = ({ isOpen, groupId, onAdded, onClose }) => {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [adding, setAdding] = useState(false);

  const { data: candidates, loading } = useCaseGroupCandidates(
    { page: 1, pageSize: 100, searchTerm: search || undefined, groupId },
    isOpen,
  );

  const toggle = useCallback((caseId: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(caseId) ? next.delete(caseId) : next.add(caseId);
      return next;
    });
  }, []);

  const handleAdd = useCallback(async () => {
    if (selected.size === 0) { notificationService.toast.warning('Select at least one case.'); return; }
    setAdding(true);
    try {
      await caseGroupService.addMembers(groupId, { caseIds: Array.from(selected) });
      setAdding(false);
      notificationService.toast.success(`Added ${selected.size} case${selected.size === 1 ? '' : 's'} to the group.`);
      setSelected(new Set());
      onAdded();
      onClose();
    } catch (err: any) {
      setAdding(false);
      notificationService.toast.error(err?.response?.data?.message || 'Failed to add members.', 6000);
    }
  }, [selected, groupId, onAdded, onClose]);

  const handleClose = useCallback(() => { setSelected(new Set()); setSearch(''); onClose(); }, [onClose]);

  const count = useMemo(() => selected.size, [selected]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg">
        {adding && (
          <div style={{ position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: 'inherit' }}>
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Adding…</span>
          </div>
        )}
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Add Cases to Group</h2>
          <button className="modal-close" onClick={handleClose} disabled={adding}><IconSystem.x size={20} /></button>
        </div>

        <div className="modal-content">
          <div style={{ marginBottom: '0.6rem' }}>
            <input type="text" className="form-input" value={search} placeholder="Search case / customer / vehicle / account…"
              onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
            Eligible cases for this event (filtered by the group's type) and not already a member.
          </div>

          <div style={{ maxHeight: 380, overflow: 'auto', border: '1px solid var(--border-color)', borderRadius: 6 }}>
            {loading ? (
              <div style={{ padding: '1.25rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem' }}>Loading…</div>
            ) : candidates.length === 0 ? (
              <div style={{ padding: '1.25rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                No eligible cases for this event.
              </div>
            ) : (
              candidates.map(c => {
                const checked = selected.has(c.caseId);
                return (
                  <label key={c.caseId} style={{
                    display: 'flex', alignItems: 'flex-start', gap: '0.6rem', padding: '0.55rem 0.7rem',
                    borderBottom: '1px solid var(--border-color)', cursor: 'pointer',
                    background: checked ? 'rgba(37,99,235,0.06)' : 'transparent',
                  }}>
                    <input type="checkbox" checked={checked} onChange={() => toggle(c.caseId)} style={{ marginTop: '0.15rem' }} />
                    <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                      <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                        {candidateLabel(c)} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>· {c.caseCode}</span>
                      </span>
                      <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
                        {c.customerName || '—'} · {c.accountNumber || '—'} · Outstanding {fmtAmount(c.currentOutstanding)}{c.dpd != null ? ` · DPD ${c.dpd}` : ''}
                      </span>
                    </span>
                  </label>
                );
              })
            )}
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={adding}>Cancel</button>
          <button className="btn btn-primary" onClick={handleAdd} disabled={adding || count === 0}>
            Add {count > 0 ? `${count} ` : ''}Case{count === 1 ? '' : 's'}
          </button>
        </div>
      </div>
    </>
  );
};

export default CaseGroupCandidatePickerModal;
